/**
 * 周指标计算模块
 */
import { WeeklyStrategy, WeeklyMetrics } from '../types';
import { calculateEffectivePrice } from './discounts';

/**


/**
 * 计算单周的运营指标
 */
export const calculateWeeklyMetrics = (
    strategy: WeeklyStrategy,
    landedCost: number,
    fbaFee: number,
    referralRate: number,
    storageFee: number
): WeeklyMetrics => {
    // 第13周只有6天，其他周7天
    const daysInWeek = strategy.weekNumber === 13 ? 6 : 7;

    // 总销量 (输入的日销即为总目标销量)
    const weeklyUnits = strategy.dailyUnits * daysInWeek;

    // Promo 销量
    const promoDailyUnits = (strategy.enablePromotion && strategy.promotionDailyUnits) ? strategy.promotionDailyUnits : 0;
    const promoWeeklyUnits = promoDailyUnits * daysInWeek;

    // 基础销量 = 总销量 - Promo销量 (确保不为负)
    const baseWeeklyUnits = Math.max(0, weeklyUnits - promoWeeklyUnits);

    // 计算实际售价（分别计算基础销量和 Promo 销量）
    // 1. 基础销量：不享受 Promo 折扣
    const baseStrategy = { ...strategy, enablePromotion: false };
    const baseEffectivePrice = calculateEffectivePrice(baseStrategy);
    const baseSales = baseEffectivePrice * baseWeeklyUnits;

    // 2. Promo 销量：享受 Promo 折扣
    // 注意：calculateEffectivePrice 中，如果 enableCoupon 为 true，会忽略 Promotion。
    // 但由于 UI 限制了 Coupon 和 Promo 互斥，这里 enablePromotion 为 true 时，enableCoupon 必为 false。
    const promoEffectivePrice = calculateEffectivePrice(strategy);
    const promoSales = promoEffectivePrice * promoWeeklyUnits;

    const sales = baseSales + promoSales;
    const effectivePrice = weeklyUnits > 0 ? sales / weeklyUnits : 0;

    // 成本计算
    const productCost = landedCost * weeklyUnits;
    const totalFbaFee = fbaFee * weeklyUnits;
    const totalStorageFee = storageFee * weeklyUnits;
    const totalReferralFee = sales * (referralRate / 100);

    // Coupon 成本（仅当启用时）
    // 1. 创建费：$5.0 (一次性/单周固定)
    // 2. 可变费：销售额 * 2.5% * 使用率 (60%)
    const COUPON_REDEMPTION_RATE = 0.60;
    const COUPON_CREATION_FEE = 5.0;
    const couponCost = strategy.enableCoupon
        ? COUPON_CREATION_FEE + (sales * 0.025 * COUPON_REDEMPTION_RATE)
        : 0;

    // 营销活动成本
    const marketingCost = strategy.marketingActivities
        ? strategy.marketingActivities.reduce((sum, activity) => sum + activity.totalCost, 0)
        : 0;

    // 广告费
    let adCost = 0;
    let adSales = 0;
    if (strategy.enableAds && strategy.cvr > 0) {
        const adSalesRatio = strategy.adSalesRatio / 100;
        const cvrDecimal = strategy.cvr / 100;

        // 广告出单量基于基础销量计算 (排除 Promo 销量)
        // 逻辑：Promo 销量通常来自站外或直接链接，不消耗站内广告预算
        const adUnits = baseWeeklyUnits * adSalesRatio;
        const clicks = adUnits / cvrDecimal;
        adCost = clicks * strategy.cpc;
        adSales = adUnits * effectivePrice;
    }

    // 利润计算
    const totalCost = productCost + totalFbaFee + totalReferralFee + totalStorageFee + couponCost + adCost + marketingCost;
    const netProfit = sales - totalCost;
    const netMargin = sales > 0 ? (netProfit / sales) * 100 : 0;

    // ACOS 和 TACOS 计算
    const acos = adSales > 0 ? (adCost / adSales) * 100 : 0;
    const tacos = sales > 0 ? (adCost / sales) * 100 : 0;

    // 促销专属利润率计算（仅在启用促销且有促销销量时）
    let promotionSales: number | undefined;
    let promotionNetProfit: number | undefined;
    let promotionNetMargin: number | undefined;

    if (strategy.enablePromotion && promoWeeklyUnits > 0 && promoSales > 0) {
        promotionSales = promoSales;

        // 按销量比例分配成本到促销部分
        const promoProductCost = landedCost * promoWeeklyUnits;
        const promoFbaFee = fbaFee * promoWeeklyUnits;
        const promoStorageFee = storageFee * promoWeeklyUnits;
        const promoReferralFee = promoSales * (referralRate / 100);

        // Coupon 成本按销售额比例分配
        const promoCouponCost = strategy.enableCoupon ? (couponCost * (promoSales / sales)) : 0;

        // 广告费不分配给促销（促销通常来自站外或直接链接）
        const promoAdCost = 0;

        // 促销利润 = 促销销售额 - 促销成本
        const promoTotalCost = promoProductCost + promoFbaFee + promoReferralFee + promoStorageFee + promoCouponCost + promoAdCost;
        promotionNetProfit = promoSales - promoTotalCost;
        promotionNetMargin = promoSales > 0 ? (promotionNetProfit / promoSales) * 100 : 0;
    }

    return {
        weekNumber: strategy.weekNumber,
        effectivePrice,
        sales,
        couponCost,
        adCost,
        netProfit,
        netMargin,
        acos,
        tacos,
        promotionSales,
        promotionNetProfit,
        promotionNetMargin,
        marketingCost
    };
};
